import React, { useCallback, useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as Tooltip from "@radix-ui/react-tooltip";
import { HelmetProvider } from "react-helmet-async";
import { pdfjs } from "react-pdf";
import { useShallow } from "zustand/react/shallow";

import { initSentry } from "./sentry";

import { useErrorHandler } from "@api/errorHandler/apiErrorHandler";
import { TOAST_CONTAINER_PROPS } from "@configs";
import { SkeletonContainer } from "@components/atoms/loader";
import { AppRouter } from "@routes/appRouter.tsx";
import { axiosClient, queryClient } from "@api/clients.ts";
import { HeadConfig } from "@/headConfig";
import { getDir } from "@utils";
import { useAxiosErrorStore } from "@/stores/axiosErrorStore";
import "./i18n/i18n";

import "./styles/main.css";
import "./assets/flags/flag-icons-min.css";
import "./assets/flags/flag-icons.css";
import "react-toastify/dist/ReactToastify.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "@/theme/useTheme";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

initSentry();

if (import.meta.env.VITE_LANGUAGE === undefined) {
  throw new Error("no VITE_LANGUAGE env");
}

export function MainRoot() {
  const { i18n } = useTranslation();

  const setError = useAxiosErrorStore(useShallow((state) => state.setError));

  const errorHandlerFunc = useCallback(
    (message: string, status: number) => {
      setError({ message, status });
    },
    [setError],
  );

  const errorHandler = useErrorHandler(errorHandlerFunc);

  useEffect(() => {
    axiosClient.interceptors.response.use(undefined, errorHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HelmetProvider>
      <HeadConfig />
      <Tooltip.Provider delayDuration={0}>
        <QueryClientProvider client={queryClient}>
          <SkeletonContainer>
            <AppRouter />
          </SkeletonContainer>
          <ReactQueryDevtools initialIsOpen={false} position={"bottom-right"} />
          <ToastContainer
            {...TOAST_CONTAINER_PROPS}
            rtl={getDir(i18n.language) === "rtl"}
          />
        </QueryClientProvider>
      </Tooltip.Provider>
    </HelmetProvider>
  );
}
